<template>
  <div class="form-cont">
    <div class="form-headline">
      Möchtest du Zusatzoptionen dazubuchen?
    </div>
    <div class="form-fields">
      <BaseGridRow no-gap>
        <BaseGridCol>
          <BaseGridRow small>
            <BaseGridCol>
              <div class="form-headline">
                1. Auswahl Deines Routers
              </div>
            </BaseGridCol>
            <BaseGridCol>
              <AppRadioCardButtons
                  class="mb-0"
                  v-model="form.addons.router"
                  :options="routerOptions"
                  :grid-template="{
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                    lg: '1fr 1fr',
                    xxl: '1fr 1fr 1fr',
                  }"
              />
            </BaseGridCol>
          </BaseGridRow>
        </BaseGridCol>
        <BaseGridCol v-if="ipOptions.length">
          <BaseGridRow small>
            <BaseGridCol>
              <div class="form-headline">
                2. IP-Netz
              </div>
            </BaseGridCol>
            <BaseGridCol>
              <AppRadioCardButtons
                  class="mb-0"
                  v-model="form.addons.ipNetworks"
                  :options="ipOptions"
                  multiple
                  :grid-template="{
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                    lg: '1fr 1fr',
                    xxl: '1fr 1fr 1fr',
                  }"
              />
            </BaseGridCol>
          </BaseGridRow>
        </BaseGridCol>
        <BaseGridCol>
          <BaseGridRow small>
            <BaseGridCol>
              <div class="form-headline">
                {{ ipOptions.length ? 3 : 2 }}. Flatrates
              </div>
            </BaseGridCol>
            <BaseGridCol>
              <AppRadioCardButtons
                  class="mb-0"
                  v-model="form.addons.flatrates"
                  :options="flatrateOptions"
                  multiple
                  :grid-template="{
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                    lg: '1fr 1fr',
                    xxl: '1fr 1fr 1fr',
                  }"
              />
            </BaseGridCol>
          </BaseGridRow>
        </BaseGridCol>
        <BaseGridCol>
          <BaseGridRow small>
            <BaseGridCol>
              <div class="form-headline">
                {{ ipOptions.length ? 4 : 3 }}. Service
              </div>
            </BaseGridCol>
            <BaseGridCol>
              <AppRadioCardButtons
                  class="mb-0"
                  v-model="form.addons.services"
                  :options="serviceOptions"
                  multiple
                  :at-least-one-of="['service-1', 'service-2']"
                  switch-excludes
                  :grid-template="{
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                    lg: '1fr 1fr',
                    xxl: '1fr 1fr 1fr',
                  }"
              />
            </BaseGridCol>
          </BaseGridRow>
        </BaseGridCol>
      </BaseGridRow>
    </div>
    <div>
      <AppOrderActions
          :labels="{
            previous: 'Zurück',
            next: hasAdditionalOptions ? 'Weiter' : 'Ohne Zusatzoptionen weiter',
          }"
          @previous="emit('previous')"
          @next="emit('next')"
          :color-next="product.branch"
          hide-required-hint
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Product from '@/classes/Product';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridRow from '@/components/BaseGridRow.vue';
import AppRadioCardButtons from '@/components/inputs/AppRadioCardButtons.vue';
import AppOrderActions from '@/components/order/order/AppOrderActions.vue';
import { formatNumber } from '@/helpers';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { computed } from 'vue';
import TelevisionProducts from '../../../../public/TelevisionProducts.json';

const props = defineProps<{
  product: Product
  priceParams: any
}>();

const form = defineModel<Record<string, any>>();
const emit = defineEmits(['previous', 'next']);

const routers = TelevisionProducts.router as TelevisionPackageInterface[];
const services = TelevisionProducts.services as TelevisionPackageInterface[];
const ipNetworks = TelevisionProducts.ipNetworks as TelevisionPackageInterface[];
const flatrates = TelevisionProducts.flatrates as TelevisionPackageInterface[];
const customerType = props.priceParams?.customerType || 'private';
const getMonthlyBasePrice = (item: TelevisionPackageInterface) => item.price?.recurring?.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0;
const getMonthlyBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getMonthlyBasePrice(item), 2);
const getOneTimeBasePrice = (item: TelevisionPackageInterface) => item.price?.oneTime?.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0;
const getOneTimeBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getOneTimeBasePrice(item), 2);

const routerOptions = routers.map((router) => ({
  value: router.id,
  label: `${getMonthlyBasePrice(router) > 0 ? '+' : ''}${getMonthlyBasePriceFormatted(router)}&thinsp;€/Monat`,
  description: router.name,
  hint: router.hint,
  customerType: router.customerType,
})).filter((option) => !Array.isArray(option.customerType) || option.customerType.includes(customerType));
const flatrateOptions = flatrates.map((flatrate) => {
  let flatrateToUse = flatrate;

  if (flatrateToUse?.isTariffConditional) return undefined;
  if (flatrateToUse?.tariffCondition) {
    if (props.product.name.trim().toLowerCase().startsWith(flatrateToUse.tariffCondition.tariff.toLowerCase())) {
      const flatrateToReplaceBy = flatrates.find((s) => s.id === flatrateToUse.tariffCondition?.replaceBy);

      if (flatrateToReplaceBy) {
        flatrateToUse = flatrateToReplaceBy;
      }
    }
  }

  return {
    value: flatrateToUse.id,
    label: `${getMonthlyBasePrice(flatrateToUse) > 0 ? '+' : ''}${getMonthlyBasePriceFormatted(flatrateToUse)}&thinsp;€/Monat`,
    description: flatrateToUse.name,
    fixed: flatrateToUse.id === 'flat-1',
    excludes: flatrateToUse.price.excludes,
    customerType: flatrateToUse.customerType,
  };
}).filter((option) => !!option && (!Array.isArray(option.customerType) || option.customerType.includes(customerType)));
const serviceOptions = services.map((service) => {
  const hasMonthly = getMonthlyBasePrice(service) > 0;
  const price = hasMonthly ? getMonthlyBasePrice(service) : getOneTimeBasePrice(service);
  const priceFormatted = hasMonthly ? `${getMonthlyBasePriceFormatted(service)}&thinsp;€/Monat` : `${getOneTimeBasePriceFormatted(service)}&thinsp;€`;

  return {
    value: service.id,
    label: `${price > 0 ? '+' : ''}${priceFormatted}`,
    description: service.name,
    excludes: service.price.excludes,
    customerType: service.customerType,
    fixed: customerType === 'business' && service.id === 'service-1',
  };
}).filter((option) => !!option && (!Array.isArray(option.customerType) || option.customerType.includes(customerType)));
const ipOptions = ipNetworks.map((ip) => ({
  value: ip.id,
  label: `${getOneTimeBasePrice(ip) > 0 ? '+' : ''}${getMonthlyBasePriceFormatted(ip)}&thinsp;€/Monat`,
  description: ip.name,
  excludes: ip.price.excludes,
  customerType: ip.customerType,
})).filter((option) => !Array.isArray(option.customerType) || option.customerType.includes(customerType));

const hasAdditionalOptions = computed(() => {
  if (form.value?.addons?.router !== routerOptions[0].value) return true;
  if ((form.value?.addons?.flatrates || []).length > 1) return true;
  if ((form.value?.addons?.ipNetworks || []).length > 1) return true;
  if ((form.value?.addons?.services || []).length >= 1 && (form.value?.addons?.services || []).join() !== 'service-1') return true;

  return false;
});
</script>

<style scoped lang="scss">
@import 'src/scss/component-helpers';

.form-cont {
  height: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr auto;

  > div {
    padding: var(--modal-card-padding-x);

    &.form-fields {
      padding: 0;

      > .app-grid__row > .app-grid__col {
        padding: var(--modal-card-padding-x);

        + .app-grid__col {
          border-top: 1px solid #ECF2F6;
        }
      }
    }

    &:last-child {
      padding-top: 10px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        padding-top: 20px;
      }
    }
  }

  .form-headline {
    font-size: 1rem;
    font-weight: 700;
    padding-bottom: 0;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: 1.25rem;
    }
  }
}
</style>
